import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { lazy } from 'utils/generic/lazy';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import {
  EAdvancedCouponsLayouts,
  EAdvancedEventsLayouts
} from 'interfaces/sportsbook';
import { SportsHeader } from '../../../../../__partials/SportsHeader';
import { TopLeagueCompactMenuItem } from 'elements/SportsbookLeftSideBar/partials/TopLeagueCompactMenuItem';
import { AdvancedCompetitionsBySportLayout } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedCompetitionsBySportLayout';
import { AdvancedCouponsLayout } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedCouponsLayout';
import { AdvancedEventsLayout } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout';
import {
  EAdvancedSportFilters,
  SportFilters
} from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedFilters/SportFilters';
import { AdvancedGamesByCompetitions } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedGamesByCompetitions';
import {
  AdvancedHeaderBreadcrumb,
  TBreadcrumbItemType
} from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedHeaderBreadcrumb';
import { AdvancedMainSportsMenu } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainSportsMenu';
import { EmptyMatches } from 'components/EmptyResultBoxes/EmptyMatches';
import { FavoritesSkeleton } from 'components/Shared/Favorites/FavoritesSkeleton';
import { NoItem } from 'components/Shared/NoItem';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useContentHeight } from 'hooks/useContentHeight';
import { useSearchParams } from 'hooks/useSearchParams';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { getFavoriteEntitiesCount } from 'store/selectors/fav-data';
import {
  getSportsbookSelectedSport,
  getSportsData
} from 'store/selectors/new-sportsbook';

const { Favorite } = lazy(
  () =>
    import(
      /* webpackChunkName: "sportsbook-favorites" */ 'components/Shared/Favorites/Favorite'
    )
);

const translationKeysByView = {
  [EAdvancedSportFilters.NOW_NEXT]: 'nowAndNext',
  [EAdvancedSportFilters.COMPETITIONS]: 'competitions',
  [EAdvancedSportFilters.COUPONS]: 'coupons'
};

export const GeneralEventsView = () => {
  const { t } = useTranslation();
  const contentHeight = useContentHeight(false);
  const configs = useSportsbookConfigs();

  const {
    layoutType: selectedLayoutInQuery,
    viewBy: selectedSportViewByFilterInQuery,
    coupon: selectedCouponInQuery
  }: {
    layoutType?: EAdvancedEventsLayouts;
    viewBy?: EAdvancedSportFilters;
    coupon?: EAdvancedCouponsLayouts;
  } = useSearchParams();

  const routeParams = useSbRouteMatch(
    `${mountPoint(
      window.getPathname()
    )}${`/:sport?/:region?/:competition?/:game?`}`
  );

  const sportsData = useSelector(getSportsData)?.[configs.type];
  const mainSportsMenuSelectedSport = useSelector(getSportsbookSelectedSport);
  const favoritesCount = useSelector(
    getFavoriteEntitiesCount
  ).sportsWithCompetitions;

  const sportInRoute = routeParams?.params?.sport;

  const isMainSportSelected = !!sportInRoute && !selectedLayoutInQuery;

  const breadcrumbItems: TBreadcrumbItemType[] = useMemo(() => {
    if (isMainSportSelected && mainSportsMenuSelectedSport?.name) {
      return [
        {
          id: 1,
          title: `${mainSportsMenuSelectedSport.name} - ${t(
            `sportsbook.${
              translationKeysByView[
                selectedSportViewByFilterInQuery ||
                  EAdvancedSportFilters.NOW_NEXT
              ]
            }`
          )}`,
          active: true
        }
      ];
    }

    if (selectedLayoutInQuery && mainSportsMenuSelectedSport) {
      return [
        {
          id: 2,
          title: `${mainSportsMenuSelectedSport.name}`,
          active: true
        }
      ];
    }

    return [
      {
        id: 1,
        title: (
          <Skeleton.Button
            active
            style={{
              width: '120px',
              height: '20px'
            }}
          />
        ),
        active: true
      }
    ];
  }, [
    isMainSportSelected,
    mainSportsMenuSelectedSport?.name,
    selectedSportViewByFilterInQuery
  ]);

  const generateContent = () => {
    if (isMainSportSelected) {
      if (
        selectedSportViewByFilterInQuery === EAdvancedSportFilters.COMPETITIONS
      ) {
        return <AdvancedCompetitionsBySportLayout />;
      }

      if (selectedSportViewByFilterInQuery === EAdvancedSportFilters.COUPONS) {
        return <AdvancedCouponsLayout />;
      }

      if (selectedCouponInQuery) {
        return <AdvancedGamesByCompetitions key={sportInRoute} />;
      }
    }

    return (
      <>
        {!selectedLayoutInQuery && isMobile() && !isMainSportSelected && (
          <div className="mobileTopLeaguesHeader">
            <TopLeagueCompactMenuItem
              showCollapse
              sportIconSize={20}
              regionIconSize={20}
            />
          </div>
        )}
        {[
          EAdvancedEventsLayouts.live,
          EAdvancedEventsLayouts.popular,
          EAdvancedEventsLayouts.upcoming
        ].map(layout => {
          if (selectedLayoutInQuery) {
            if (selectedLayoutInQuery === layout) {
              return <AdvancedEventsLayout key={layout} layoutType={layout} />;
            }

            return null;
          }

          if (
            sportInRoute &&
            layout === EAdvancedEventsLayouts.popular &&
            !routeParams.params.game
          ) {
            return null;
          }

          return <AdvancedEventsLayout key={layout} layoutType={layout} />;
        })}
      </>
    );
  };

  const shouldShowMainBreadcrumb =
    (isMainSportSelected || !!selectedLayoutInQuery) && !selectedCouponInQuery;

  if (sportsData === undefined) {
    return (
      <div
        style={{ height: isMobile() ? contentHeight : '100%' }}
        className={cc([
          'emptyMatchesWrapperAdvanced',
          { emptyMatchesWrapperMobile: isMobile() }
        ])}
      >
        <EmptyMatches isLive withLink />
      </div>
    );
  }

  return (
    <>
      <AdvancedMainSportsMenu />
      {isMobile() && sportInRoute === 'favorite' && (
        <SportsHeader sportAlias={sportInRoute} showTimeFilter={false} />
      )}
      {isMobile() && sportInRoute === 'favorite' ? (
        favoritesCount ? (
          <Suspense fallback={<FavoritesSkeleton />}>
            <Favorite />
          </Suspense>
        ) : (
          <NoItem
            className="mobileFavoritesEmptyView"
            title={t('sportsbook.noGame')}
            text={t('sportsbook.clickToAddFavorite')}
            size="medium"
          />
        )
      ) : (
        <>
          {shouldShowMainBreadcrumb && (
            <AdvancedHeaderBreadcrumb
              additionalNavigationItems={breadcrumbItems}
            />
          )}
          {isMainSportSelected && !selectedCouponInQuery && <SportFilters />}
          {generateContent()}
        </>
      )}
    </>
  );
};
